import { getArtikelDetail, listArtikel, type ArtikelRow, type ListArtikelParams } from "@/lib/artikel";

export const PT_P910BT_DPI = 360;
export const PT_P910BT_HEAD_PINS = 560;
export const PT_P910BT_RASTER_BYTES = 70;
export const PT_P910BT_USB_VENDOR_ID = 0x04f9;
export const PT_P910BT_USB_PRODUCT_ID = 0x20c7;

export const ARTIKEL_ETIKETT_MAX = 80;
export const ARTIKEL_ETIKETT_TAPE_KEY = "ernsthaus.artikel.etikett.tapeMm";
export const ARTIKEL_ETIKETT_LENGTH_KEY = "ernsthaus.artikel.etikett.lengthMm";
export const ARTIKEL_ETIKETT_LENGTH_MIN = 25;
export const ARTIKEL_ETIKETT_LENGTH_MAX = 200;
export const ARTIKEL_ETIKETT_LENGTH_DEFAULT = 100;
export const ARTIKEL_ETIKETT_COPIES_MAX = 20;
export const ARTIKEL_ETIKETT_COPIES_DEFAULT = 1;

export type TapeMm = 12 | 18 | 24 | 36;

export interface TapeSpec {
  tapeMm: TapeMm;
  printDots: number;
  leftPins: number;
  rightPins: number;
}

/** Druckbereich laut Brother Raster Command Reference PT-P910BT. */
export const TAPE_SPECS: Record<TapeMm, TapeSpec> = {
  12: { tapeMm: 12, printDots: 150, leftPins: 197, rightPins: 213 },
  18: { tapeMm: 18, printDots: 234, leftPins: 155, rightPins: 171 },
  24: { tapeMm: 24, printDots: 320, leftPins: 112, rightPins: 128 },
  36: { tapeMm: 36, printDots: 454, leftPins: 45, rightPins: 61 },
};

export const TAPE_OPTIONS: TapeMm[] = [18, 24, 36, 12];

export interface ArtikelEtikettItem {
  id: number;
  heroNr: string;
  name: string;
}

export interface ArtikelEtikettLocation {
  origin: string;
  pathname: string;
}

export function isTapeMm(value: number): value is TapeMm {
  return value === 12 || value === 18 || value === 24 || value === 36;
}

export function getTapeSpec(tapeMm: number): TapeSpec {
  if (!isTapeMm(tapeMm)) {
    throw new Error(`Nicht unterstützte Bandbreite: ${tapeMm} mm`);
  }
  return TAPE_SPECS[tapeMm];
}

export function mmToDots(mm: number, dpi = PT_P910BT_DPI): number {
  return Math.round((mm * dpi) / 25.4);
}

export function dotsToMm(dots: number, dpi = PT_P910BT_DPI): number {
  return (dots * 25.4) / dpi;
}

export function toArtikelEtikettItem(row: {
  id: number;
  hero_nr?: string | null;
  hero_base_name?: string | null;
}): ArtikelEtikettItem {
  const heroNr = String(row.hero_nr ?? "").trim();
  return {
    id: row.id,
    heroNr: heroNr || `#${row.id}`,
    name: String(row.hero_base_name ?? "").trim(),
  };
}

export function buildArtikelEtikettUrl(
  artikelId: number,
  loc: ArtikelEtikettLocation,
): string {
  const path = loc.pathname.includes("/bestellwesen/") ? "/bestellwesen/artikel" : "/artikel";
  return `${loc.origin}${path}?id=${artikelId}`;
}

export type ArtikelEtikettScan =
  | { kind: "artikelId"; id: number }
  | { kind: "ean"; ean: string };

function isArtikelEtikettPath(pathname: string): boolean {
  const path = pathname.replace(/\/+$/, "").toLowerCase();
  return path.endsWith("/artikel") || path.endsWith("/bestellwesen/artikel");
}

function positiveInt(value: string | null | undefined): number | null {
  const n = Number(String(value ?? "").trim());
  return Number.isInteger(n) && n > 0 ? n : null;
}

/** Liest die Artikel-ID aus einem Etiketten-QR (`/artikel?id=` / `/bestellwesen/artikel?id=`). */
export function parseArtikelIdFromEtikettUrl(raw: string): number | null {
  const text = String(raw || "").trim();
  if (!text) return null;
  let url: URL;
  try {
    url = new URL(text);
  } catch {
    try {
      url = new URL(text, "https://app.ernsthaus.de");
    } catch {
      return null;
    }
  }
  if (!isArtikelEtikettPath(url.pathname)) return null;
  return positiveInt(url.searchParams.get("id"));
}

/**
 * Erkennt den Inhalt eines gescannten Artikel-Etiketts:
 * Deep-Link mit `id`, kurze numerische Artikel-ID oder EAN/GTIN.
 */
export function parseArtikelEtikettScan(raw: string): ArtikelEtikettScan | null {
  const text = String(raw || "").trim();
  if (!text) return null;

  const fromUrl = parseArtikelIdFromEtikettUrl(text);
  if (fromUrl) return { kind: "artikelId", id: fromUrl };

  if (/^\d{1,7}$/.test(text)) {
    const id = positiveInt(text);
    if (id) return { kind: "artikelId", id };
  }

  const digits = text.replace(/\D/g, "");
  if (digits.length === 8 || digits.length === 12 || digits.length === 13 || digits.length === 14) {
    return { kind: "ean", ean: digits };
  }

  return null;
}

export function currentArtikelEtikettLocation(): ArtikelEtikettLocation {
  if (typeof window === "undefined") {
    return { origin: "", pathname: "/bestellwesen/artikel" };
  }
  return { origin: window.location.origin, pathname: window.location.pathname };
}

export function loadStoredTapeMm(): TapeMm {
  if (typeof window === "undefined") return 24;
  const raw = Number(window.localStorage.getItem(ARTIKEL_ETIKETT_TAPE_KEY));
  return isTapeMm(raw) ? raw : 24;
}

export function storeTapeMm(tapeMm: TapeMm): void {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(ARTIKEL_ETIKETT_TAPE_KEY, String(tapeMm));
}

export function clampLabelLengthMm(value: number): number {
  if (!Number.isFinite(value)) return ARTIKEL_ETIKETT_LENGTH_DEFAULT;
  return Math.min(
    ARTIKEL_ETIKETT_LENGTH_MAX,
    Math.max(ARTIKEL_ETIKETT_LENGTH_MIN, Math.round(value)),
  );
}

export function clampEtikettCopies(value: number): number {
  if (!Number.isFinite(value)) return ARTIKEL_ETIKETT_COPIES_DEFAULT;
  return Math.min(ARTIKEL_ETIKETT_COPIES_MAX, Math.max(1, Math.round(value)));
}

export function loadStoredLengthMm(): number {
  if (typeof window === "undefined") return ARTIKEL_ETIKETT_LENGTH_DEFAULT;
  return clampLabelLengthMm(Number(window.localStorage.getItem(ARTIKEL_ETIKETT_LENGTH_KEY)));
}

export function storeLengthMm(lengthMm: number): void {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(ARTIKEL_ETIKETT_LENGTH_KEY, String(clampLabelLengthMm(lengthMm)));
}

export interface ResolveArtikelEtikettParams {
  mode: "ids" | "allMatching";
  ids: number[];
  excludeIds: number[];
  knownRows: Array<Pick<ArtikelRow, "id" | "hero_nr" | "hero_base_name">>;
  listParams: ListArtikelParams;
  limit?: number;
}

export async function resolveArtikelEtikettItems(
  params: ResolveArtikelEtikettParams,
): Promise<{ items: ArtikelEtikettItem[]; truncated: boolean; total: number }> {
  const limit = params.limit ?? ARTIKEL_ETIKETT_MAX;
  const known = new Map(params.knownRows.map((row) => [row.id, toArtikelEtikettItem(row)]));

  if (params.mode === "ids") {
    const uniqueIds = [...new Set(params.ids.filter((id) => id > 0))];
    const items: ArtikelEtikettItem[] = [];
    for (const id of uniqueIds.slice(0, limit)) {
      const knownItem = known.get(id);
      if (knownItem) {
        items.push(knownItem);
        continue;
      }
      const detail = await getArtikelDetail(id);
      items.push(toArtikelEtikettItem(detail.artikel));
    }
    return {
      items,
      truncated: uniqueIds.length > limit,
      total: uniqueIds.length,
    };
  }

  const excluded = new Set(params.excludeIds);
  const items: ArtikelEtikettItem[] = [];
  let offset = 0;
  let total = 0;
  const pageSize = 200;

  while (items.length < limit) {
    const page = await listArtikel({
      ...params.listParams,
      limit: pageSize,
      offset,
    });
    total = page.total;
    if (!page.items.length) break;
    for (const row of page.items) {
      if (excluded.has(row.id)) continue;
      items.push(known.get(row.id) ?? toArtikelEtikettItem(row));
      if (items.length >= limit) break;
    }
    offset += page.items.length;
    if (offset >= page.total) break;
  }

  const available = Math.max(0, total - excluded.size);
  return {
    items,
    truncated: available > items.length,
    total: available,
  };
}
